import { makeid } from 'utils/helpers/makeid';

import {
  booleanOptions,
  notEscalatedOptions,
  nursingInterventionOptions,
  stringBooleanOptions,
  commonFactors,
} from './common';

import type { TFormItem, IFormItemOption } from '../types';

export const tachycardiaSymptoms: IFormItemOption<string>[] = [
  { label: 'Chest pain', value: 'chestPain', required: true, key: makeid(10) },
  {
    label: 'Dizziness/Lightheadedness',
    value: 'dizziness',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Shortness of breath',
    value: 'shortnessOfBreath',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Pounding heartbeat/palpitations',
    value: 'heartbeat',
    key: makeid(10),
  },
];

export const bradycardiaSymptoms: IFormItemOption<string>[] = [
  { label: 'Chest pain', value: 'chestPain', required: true, key: makeid(10) },
  {
    label: 'Confusion/memory problems',
    value: 'memoryProblems',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Dizziness/Lightheadedness',
    value: 'dizziness',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Fainting or near-fainting',
    value: 'fainting',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Shortness of breath',
    value: 'shortnessOfBreath',
    required: true,
    key: makeid(10),
  },
  { label: 'Fatigue', value: 'fatigue', key: makeid(10) },
  {
    label: 'Easily tiring during physical activity',
    value: 'physicalActivity',
    key: makeid(10),
  },
];

export const factorsAssessment: IFormItemOption<string>[] = [
  ...commonFactors,
  {
    label:
      'Smoked/used tobacco or nicotine products within 30 minutes of reading',
    value: 'smoked',
    key: makeid(10),
  },
  {
    label: 'Drank alcohol or caffeine within 30 minutes of reading',
    value: 'drank',
    key: makeid(10),
  },
  {
    label: 'Exercised/was physically active within 30 minutes of reading',
    value: 'activity',
    key: makeid(10),
  },
  {
    label: 'Took multiple readings back to back',
    value: 'multiple',
    key: makeid(10),
  },
  {
    label: 'Took reading over clothes',
    value: 'overClothes',
    key: makeid(10),
  },
  {
    label: 'Cuff misplacement',
    value: 'misplacement',
    key: makeid(10),
  },
  {
    label: 'Sleep mat device issue',
    value: 'sleepMat',
    key: makeid(10),
  },
];

export const assessmentPulseForm: TFormItem[] = [
  {
    key: makeid(10),
    title: '#1 Did you speak to patient?',
    description:
      'Choose other if unlisted family member & type in their relationship. (Ex: Daughter in law) Add Name of Individual you spoke to in other.',
    type: 'radio',
    required: true,
    name: 'patient',
    options: [
      { value: 'Patient', label: 'Patient', key: makeid(10) },
      { value: 'Spouse', label: 'Spouse', key: makeid(10) },
      { value: 'Caregiver', label: 'Caregiver', key: makeid(10) },
      {
        value: 'Emergency Contact',
        label: 'Emergency Contact',
        key: makeid(10),
      },
      { value: null, label: 'Other', key: makeid(10) },
    ],
    extra: [
      {
        type: 'doubleRowInput',
        name: 'other',
        key: makeid(10),
        options: [
          { label: 'Other Member', value: 'other', key: makeid(10) },
          { label: 'Relationship', value: 'relationship', key: makeid(10) },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#2 Mental Status Assessment',
    description:
      'Oriented and Coherent 1: Person 2: Person, place 3: Person, place, time 4: Person, place, time, situation?',
    type: 'radio',
    required: false,
    name: 'AOC',
    options: [
      { value: 'x1', label: 'x1', key: makeid(10) },
      { value: 'x2', label: 'x2', key: makeid(10) },
      { value: 'x3', label: 'x3', key: makeid(10) },
      { value: 'x4', label: 'x4', key: makeid(10) },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#3 Appointments',
    type: 'datepicker',
    name: 'datepicker',
    required: false,
    options: [
      {
        value: 'last',
        label: "Last Doctor's Appointment",
        type: 'max',
        key: makeid(10),
      },
      {
        value: 'next',
        label: "Next Doctor's Appointment",
        type: 'min',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#4 Patient is able to take another reading at time of call?',
    subtitle: 'Is the reading still out of bounds?',
    type: 'radio',
    required: true,
    name: 'isOneMoreReading',
    options: stringBooleanOptions,
    extra: [
      {
        type: 'radio',
        name: 'bounds',
        label: 'Is the reading still out of bounds?',
        options: booleanOptions,
        divider: true,
        key: makeid(10),
      },
      {
        type: 'input',
        name: 'repeatReadingWas',
        key: makeid(10),
        options: [
          {
            label: 'The repeat reading was:',
            value: 'repeatReadingWas',
            key: makeid(10),
          },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#5 Signs and Symptoms of Tachycardia',
    type: 'radio-list',
    name: 'symptoms',
    required: true,
    options: tachycardiaSymptoms,
    extra: [
      {
        name: 'symptomsOnset',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['max'],
  },
  {
    key: makeid(10),
    title: '#5 Signs and Symptoms of Bradycardia',
    type: 'radio-list',
    name: 'symptoms',
    required: false,
    options: bradycardiaSymptoms,
    extra: [
      {
        name: 'symptomsOnset',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min'],
  },
  {
    key: makeid(10),
    title: '#6 Did patient take medications before/after the reading?',
    type: 'radio',
    name: 'medicationTiming',
    required: false,
    options: [
      { label: 'Before', value: 'before', key: makeid(10) },
      { label: 'After', value: 'after', key: makeid(10) },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#7 Did patient take routine medications as prescribed?',
    type: 'radio',
    required: false,
    name: 'isTakeMedications',
    options: stringBooleanOptions,
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#8 Did patient take any prescribed PRN medications?',
    type: 'radio',
    required: false,
    name: 'isTakePrnMedications',
    options: stringBooleanOptions,
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#9 Additional Details',
    type: 'text',
    required: false,
    name: 'details',
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#10 Factors Assessment',
    type: 'radio-list',
    required: false,
    options: factorsAssessment,
    name: 'factorsAssessment',
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#11 Nursing Intervention',
    type: 'checkbox',
    name: 'nursingIntervention',
    required: true,
    options: nursingInterventionOptions,
    extra: [
      {
        type: 'labelCheckbox',
        name: 'notEscalated',
        key: makeid(10),
        label: 'Not Escalated',
        options: notEscalatedOptions,
        divider: true,
      },
      {
        name: 'interventionDetails',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#12 Patient Understanding',
    description:
      'Patient verbalized understanding and is agreeable to nursing recommendation?',
    type: 'radio',
    required: false,
    name: 'verbalized',
    options: booleanOptions,
    readingTypeFor: ['min', 'max'],
  },
];
